// IZTeach units the teacher opened during one period, so the strip can switch back to them.
// Keyed by period, persisted so a reload of the same period keeps its strip.
// Generated documents are shared across periods, so removing one only hides it for that period.

import { create } from 'zustand';

// the .ts extension keeps this loadable by node --test, which does not resolve bare specifiers
import { readJson, writeJson } from '../../api/storage.ts';
import type { BoardDocument } from './documents.ts';

const OPENED_STORAGE_KEY = 'izqlt.board-opened';
/** Generated documents the teacher took off one period's strip; the documents themselves stay. */
const HIDDEN_STORAGE_KEY = 'izqlt.board-hidden';
/** Oldest rows drop first, so the key cannot grow without bound across a school year. */
export const OPENED_LIMIT = 60;

export interface OpenedDocument {
  /** `date/index/classId` - the board route that owns the row. */
  period: string;
  id: string;
  title: string;
  kind: BoardDocument['kind'];
}

export function periodKey(date: string, index: number, classId: string): string {
  return date + '/' + index + '/' + classId;
}

function usable(row: unknown): row is OpenedDocument {
  const item = row as OpenedDocument | null;
  if (!item || typeof item !== 'object') return false;
  return typeof item.period === 'string' && typeof item.id === 'string' && item.id.length > 0 && typeof item.title === 'string'
    && (item.kind === 'slide' || item.kind === 'video' || item.kind === 'quiz');
}

/** A generated document (quiz or upload) hidden from one period's strip. */
export interface HiddenDocument {
  period: string;
  id: string;
}

function usableHidden(row: unknown): row is HiddenDocument {
  const item = row as HiddenDocument | null;
  if (!item || typeof item !== 'object') return false;
  return typeof item.period === 'string' && typeof item.id === 'string' && item.id.length > 0;
}

function hydrate(): OpenedDocument[] {
  const stored = readJson<unknown>(OPENED_STORAGE_KEY);
  return Array.isArray(stored) ? stored.filter(usable) : [];
}

function hydrateHidden(): HiddenDocument[] {
  const stored = readJson<unknown>(HIDDEN_STORAGE_KEY);
  return Array.isArray(stored) ? stored.filter(usableHidden) : [];
}

/** The rows without this period's entry for `id`; other periods keep theirs. */
export function withoutOpened(rows: OpenedDocument[], period: string, id: string): OpenedDocument[] {
  return rows.filter((row) => row.period !== period || row.id !== id);
}

/** Marks `id` hidden for this period only, idempotent and capped like the opened rows. */
export function hideIn(hidden: HiddenDocument[], period: string, id: string): HiddenDocument[] {
  if (hidden.filter((row) => row.period === period && row.id === id).length > 0) return hidden;
  return hidden.concat([{ period, id }]).slice(-OPENED_LIMIT);
}

/** Un-hides `id` for this period, returning the same array when there was nothing to drop. */
export function revealIn(hidden: HiddenDocument[], period: string, id: string): HiddenDocument[] {
  const next = hidden.filter((row) => row.period !== period || row.id !== id);
  return next.length === hidden.length ? hidden : next;
}

/** Generated documents minus those hidden from this period's strip. */
export function visibleDocuments(items: BoardDocument[], hidden: HiddenDocument[], period: string): BoardDocument[] {
  return items.filter((item) => hidden.filter((row) => row.period === period && row.id === item.id).length === 0);
}

export interface OpenedDocumentState {
  rows: OpenedDocument[];
  /** Adds the unit to its period, or refreshes its title in place - the strip order is open order. */
  record: (row: OpenedDocument) => void;
  hidden: HiddenDocument[];
  /** Takes `id` off this period's strip only: an opened unit loses its row, a generated one is hidden. */
  remove: (period: string, id: string, generated: boolean) => void;
  /** Puts a hidden generated document back on this period's strip once it is opened again. */
  reveal: (period: string, id: string) => void;
  clear: () => void;
}

export const useOpenedDocumentStore = create<OpenedDocumentState>((set, get) => ({
  rows: hydrate(),

  record(row) {
    const rows = get().rows;
    const existing = rows.filter((item) => item.period === row.period && item.id === row.id)[0];
    if (existing && existing.title === row.title && existing.kind === row.kind) return;
    const next = existing
      ? rows.map((item) => (item === existing ? row : item))
      : rows.concat([row]).slice(-OPENED_LIMIT);
    set({ rows: next });
    writeJson(OPENED_STORAGE_KEY, next);
  },

  hidden: hydrateHidden(),

  remove(period, id, generated) {
    const rows = withoutOpened(get().rows, period, id);
    const hidden = generated ? hideIn(get().hidden, period, id) : get().hidden;
    set({ rows, hidden });
    writeJson(OPENED_STORAGE_KEY, rows);
    writeJson(HIDDEN_STORAGE_KEY, hidden);
  },

  reveal(period, id) {
    const hidden = revealIn(get().hidden, period, id);
    if (hidden === get().hidden) return;
    set({ hidden });
    writeJson(HIDDEN_STORAGE_KEY, hidden);
  },

  clear() {
    set({ rows: [], hidden: [] });
    writeJson(OPENED_STORAGE_KEY, []);
    writeJson(HIDDEN_STORAGE_KEY, []);
  },
}));

/** The period's opened units as strip documents, in the order they were opened. */
export function openedDocuments(rows: OpenedDocument[], period: string): BoardDocument[] {
  return rows
    .filter((row) => row.period === period)
    .map((row) => ({ id: row.id, title: row.title, kind: row.kind, pageCount: 1 }));
}
